import { mdiAlertCircleOutline } from '@mdi/js'
import Icon from '@mdi/react'
import IconAdd from '@mui/icons-material/AddCircleOutline'
import Button from '@mui/material/Button'
import { PPDrawer } from 'components/PPDrawer'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import ProfileAndListSelector from 'routes/publishing/components/ProfileAndListSelector'
import { connectedDestinationsSelector } from 'services/destinations'
import { PostDestination } from 'shared'
import styles from './PlanProfileSelector.pcss'


interface PlanProfileSelectorProps {
  currentSelectedIds: string[]
  planName: string
  showWarning: boolean
  // selectedProfileIds: string[]
  // onSelectionChange: (ids: string[]) => void
  onConfirm: (ppids: string[]) => void
}

export function PlanProfileSelector(props: PlanProfileSelectorProps) {
  const { currentSelectedIds, onConfirm } = props
  const [selectedProfileIds, setSelectedProfileIds] = React.useState<string[]>([])
  const profiles: Record<string, PostDestination> = useSelector(connectedDestinationsSelector)
  const visibleProfiles = Object.keys(profiles).reduce((acc, id) => {
    if (!currentSelectedIds.includes(id)) {
      acc[id] = profiles[id]
    }
    return acc
  }, {} as Record<string, PostDestination>)
  const [activeView, setActiveView] = React.useState<'grid' | 'list' | 'network'>('grid')
  const [open, setOpen] = React.useState(false)

  const selectAll = () => {
    setSelectedProfileIds(Object.keys(visibleProfiles))
  }

  const openDropdown = (e: React.MouseEvent) => {
    e.stopPropagation()
    setOpen(true)
  }

  const closeDropdown = () => {
    setOpen(false)
    setSelectedProfileIds([])
  }

  const submit = () => {
    onConfirm(selectedProfileIds.map(id => profiles[id].ppid).filter(Boolean))
    closeDropdown()
  }

  /* eslint-disable max-len */
  const warningText = selectedProfileIds.length === 1
    ? 'Adding the above profile to this posting plan will (1) remove it from its current plan and (2) delete that plan if no other profiles are using it.'
    : 'Adding the above profiles to this posting plan will (1) remove them from their current plans and (2) delete those plans if no other profiles are using them.'
  /* eslint-enable max-len */

  const footer = (
    <div className={styles['footer-actions']}>
      <Button onClick={closeDropdown}>
        <FormattedMessage id="actions.cancel" />
      </Button>
      <Button color="primary" variant="contained" disabled={selectedProfileIds.length === 0} onClick={submit}>
        <FormattedMessage id="actions.confirm" />
      </Button>
    </div>
  )

  return (
    <div className={styles.container} data-testid="plan-profile-selector">
      <div className={styles['btn-add']} data-testid="btn-popup" onClick={openDropdown}>
        <IconAdd className={styles['icon-add']} />
      </div>
      <PPDrawer
        open={open}
        onClose={closeDropdown}
        size="sm"
        data-testid="plan-profile-selector-drawer"
        title={`Add these profiles to ${props.planName}`}
        footer={footer}
      >
        <ProfileAndListSelector
          profiles={visibleProfiles}
          lists={{}}
          withLists={false}
          selectedProfileIds={selectedProfileIds}
          multipleProfileSelection
          activeView={activeView}
          allowEmptySelection
          paperProps={{ elevation: 0 }}
          className={styles['profiles-container']}
          setActiveView={setActiveView}
          onSelectedProfilesChange={setSelectedProfileIds}
        />
        {Object.keys(visibleProfiles).length > 0 && (
          <div className={styles['select-all-bar']}>
            <span role="button" className={styles['btn-select']} onClick={selectAll}>
              <FormattedMessage id="actions.select-all" />
            </span>
          </div>
        )}
        {props.showWarning && selectedProfileIds.length > 0 && (
          <div className={styles['hint-box']}>
            <div><Icon path={mdiAlertCircleOutline} className={styles['icon-err']} size="28px" /></div>
            <ul className={styles.hint}>
              <li>{warningText}</li>
            </ul>
          </div>
        )}
      </PPDrawer>
    </div>
  )
}

export default PlanProfileSelector
